import { FormEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { SyncLoader } from 'react-spinners';
import { toast } from 'sonner';
import ThemeConstants from '../../../../config/theme/theme_constants';
import { useGetEndQuestionCourse } from '../../domain/hooks/use_question_course';
import { useFinishTheme } from '../../domain/hooks/use_finish_theme';
import { useGetUserEndAnswers } from '../../domain/hooks/use_get_user_answers';
import { useGetResourceByThemeId } from '../../domain/hooks/use_resources_by_theme_id';
import BackArrowBlackIcon from '../../../../assets/images/arrow_back_black_icon.svg?react';
import { UserInterface } from '../../../../core/components/user/domain/interfaces/user_interface';
import { CareerStatusEnum } from '../../../../core/enum/career_status_enum';
import { getUserThemeByThemeId } from '../../domain/utils/get_current_user_theme';
import { useTranslation } from '../../../../i18n/useTranslation';
import { Trans } from 'react-i18next';

const EndQuizzPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = ThemeConstants();
  const { themeId } = useParams();
  const navigate = useNavigate();
  const { experience, company, careerStatus, course_theme } = useOutletContext<UserInterface>();

  useEffect(() => {
    if (careerStatus === CareerStatusEnum.Start) {
      navigate('/career/start', { replace: true });
    }
  }, [careerStatus, navigate]);
  const { data: questions, isPending: isFetchingQuestions } = useGetEndQuestionCourse();
  const { data: existingAnswers, isPending: isFetchingAnswers } = useGetUserEndAnswers(themeId);
  const { data: resources, isPending: isFetchingResources } = useGetResourceByThemeId(themeId);
  const { mutate: finishTheme, isPending: isFinishing } = useFinishTheme();

  const hasViewedAllResources = useMemo(() => {
    if (!resources) return false;
    return resources.every(r => r.viewed);
  }, [resources]);

  const requiredExp = useMemo(() => {
    return company?.limits?.pointsFinishTheme ?? 200;
  }, [company?.limits?.pointsFinishTheme]);

  useEffect(() => {
    const userTheme = getUserThemeByThemeId(course_theme, themeId, company?.id);

    if (userTheme?.status === 'completed') {
      navigate(`/career/${themeId}/certificate`, { replace: true });
      return;
    }
    if (isFetchingResources) return;
    const blocked =
      company?.permissions.course !== true ||
      experience.courseExp < requiredExp ||
      !hasViewedAllResources;
    if (blocked) {
      toast.error(t('course.quiz.blocked', { points: requiredExp }));
      navigate(themeId ? `/career/${themeId}/resources` : '/career/start', { replace: true });
    }
  }, [company?.id, company?.permissions.course, experience.courseExp, hasViewedAllResources, isFetchingResources, navigate, themeId, requiredExp, course_theme, t]);

  const existingByQuestionId = useMemo(() => {
    const map = new Map<string, { _id?: string; answer: string }>();
    (existingAnswers ?? []).forEach(a => map.set(a._questionId, { _id: a._id, answer: a.answer }));
    return map;
  }, [existingAnswers]);

  const orderedQuestions = useMemo(
    () => (questions ? [...questions].sort((a, b) => a.order - b.order) : []),
    [questions]
  );

  const borderColors = useMemo(
    () => [theme.UvibesOrange, theme.UvibesGreen, theme.UvibesPink, theme.UvibesBlue, theme.UvibesBlue],
    [theme]
  );

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const textareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const formRef = useRef<HTMLFormElement | null>(null);

  const LINE_MIN = 44;
  const LINE_MAX = 144;

  const autosize = useCallback((el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  const fitToHeight = useCallback(() => {
    const form = formRef.current;
    const els = Object.values(textareaRefs.current).filter(Boolean) as HTMLTextAreaElement[];
    if (!form || els.length === 0) return;
    els.forEach(el => { el.style.minHeight = `${LINE_MIN}px`; autosize(el); });
    const kids = form.children;
    if (kids.length === 0) return;
    const top = kids[0].getBoundingClientRect().top;
    const contentHeight = kids[kids.length - 1].getBoundingClientRect().bottom - top;
    const page = form.parentElement;
    const paddingBottom = page ? parseFloat(getComputedStyle(page).paddingBottom) || 0 : 0;
    const available = window.innerHeight - top - paddingBottom;
    const free = available - contentHeight;
    const per = free > 0 ? Math.floor(free / els.length) : 0;
    const min = Math.min(LINE_MIN + per, LINE_MAX);
    els.forEach(el => { el.style.minHeight = `${min}px`; });
  }, [autosize]);

  const formReady =
    !isFetchingQuestions && !isFetchingAnswers && !isFetchingResources && orderedQuestions.length > 0;

  useLayoutEffect(() => {
    fitToHeight();
  }, [fitToHeight, formReady]);

  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    const ro = new ResizeObserver(() => fitToHeight());
    ro.observe(form);
    window.addEventListener('resize', fitToHeight);
    document.fonts?.ready.then(fitToHeight);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', fitToHeight);
    };
  }, [fitToHeight, formReady]);

  const handleChange = useCallback((id: string, value: string) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
  }, []);

  const handleSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();
    if (isFinishing || orderedQuestions.length === 0) return;
    const newAnswers = orderedQuestions
      .filter(q => existingByQuestionId.get(q._id)?._id === undefined)
      .map(q => ({ _questionId: q._id, answer: (answers[q._id] ?? '').trim() }));
    const missing = newAnswers.some(a => !a.answer);
    if (missing) {
      toast.error(t('course.quiz.answer_all'));
      return;
    }
    if (newAnswers.length === 0) {
      navigate(`/career/${themeId}/certificate`);
      return;
    }
    finishTheme(
      { answers: newAnswers },
      {
        onSuccess: () => navigate(`/career/${themeId}/certificate`, { replace: true }),
      }
    );
  }, [answers, orderedQuestions, existingByQuestionId, isFinishing, finishTheme, navigate, themeId, t]);

  return (
    <div className="min-h-dvh pt-[max(5.6dvh,68px)] max-lg:px-[6%] lg:px-[1%] flex flex-col gap-4 items-center relative pb-12">
      <BackArrowBlackIcon className="absolute top-8 start-5 cursor-pointer" width={20} height={20} onClick={() => navigate(-1)}/>
      <div className="w-full max-w-200 flex flex-col gap-5 mb-5">
        <h1 className="text-2xl font-bold leading-tight text-center" style={{ color: theme.UvibesPink }}>{t('course.quiz.final_title')}</h1>
        <p className="text-sm font-medium text-center leading-relaxed" style={{ color: theme.UvibesGray }}>
          <Trans i18nKey="course.quiz.final_intro" />
        </p>
      </div>

      {(isFetchingQuestions || isFetchingAnswers || isFetchingResources) && (
        <div className="flex flex-col w-full items-center justify-center min-h-90">
          <SyncLoader color={theme.UvibesPink} size={24} speedMultiplier={0.5} />
        </div>
      )}

      {!isFetchingQuestions && !isFetchingAnswers && !isFetchingResources && orderedQuestions.length > 0 && (
        <form ref={formRef} onSubmit={handleSubmit} className="w-full max-w-200 lg:max-w-250 xl:max-w-300 flex-1 flex flex-col gap-5">
          {orderedQuestions.map((q, i) => {
            const color = q.color || borderColors[i % borderColors.length];
            const existing = existingByQuestionId.get(q._id);
            const isLocked = existing?._id !== undefined;
            const value = isLocked ? existing!.answer : (answers[q._id] ?? '');
            return (
              <div key={q._id} className="flex flex-col gap-2">
                <label htmlFor={`answer-${q._id}`} className="font-bold text-sm leading-snug">
                  {q.question}
                </label>
                <textarea
                  id={`answer-${q._id}`}
                  ref={(el) => {
                    textareaRefs.current[q._id] = el;
                    autosize(el);
                  }}
                  placeholder={t('course.quiz.answer_placeholder')}
                  value={value}
                  onChange={(e) => handleChange(q._id, e.target.value)}
                  disabled={isFinishing}
                  readOnly={isLocked}
                  rows={1}
                  className="w-full rounded-2xl bg-white px-4 py-3 text-sm outline-none placeholder:italic resize-none overflow-hidden read-only:bg-gray-100 read-only:cursor-not-allowed"
                  style={{ border: `1.5px solid ${color}` }}
                  onInput={(e) => autosize(e.currentTarget)}
                />
              </div>
            );
          })}

          <div className="flex justify-center pt-6">
            <button
              type="submit"
              disabled={isFinishing}
              className="text-white font-bold rounded-full px-10 py-3 cursor-pointer disabled:opacity-60"
              style={{ backgroundColor: theme.UvibesPink }}
            >
              {isFinishing ? t('course.quiz.sending') : t('course.quiz.send_evaluation')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default EndQuizzPage;
